/**
 * What: Defines the product-field binding contract as shared types, Zod schemas, and pure resolver functions.
 * Transport / data-flow: Shared contract used during Shopify study creation, stimulus planning, and applying recommendations.
 *
 * Algorithm or layout: Maps different stimulus element types (such as claims, prices, or images) to explicit (action, target) pairs,
 * ensuring no silent defaults or unhandled types (everything unmappable explicitly resolves to advisory:none).
 */
import * as z from 'zod';
import { resolveShopifyTreatmentField } from './shopify-treatment-field.js';
export const productFieldActionSchema = z.enum([
    'append_block',
    'replace',
    'set_price',
    'add_featured_media',
    'advisory',
]);
export const productFieldTargetSchema = z.enum(['description_html', 'variant_price', 'product_media', 'none']);
export const productFieldBindingResolutionSchema = z.object({
    action: productFieldActionSchema,
    target: productFieldTargetSchema,
});
/**
 * Display-only product context for the results preview. Never compared for drift and never read
 * by `extractBaselineValueForTarget` — both switch on `resolution.target`, so widening this can
 * never change which cards go stale. Captured once per packet, on the first binding written, and
 * hoisted onto the change list for rendering.
 */
export const productFieldPreviewContextSchema = z.object({
    title: z.string().nullable().optional(),
    descriptionHtml: z.string().nullable().optional(),
    imageUrl: z.string().nullable().optional(),
    displayPrice: z.string().nullable().optional(),
});
export const productFieldBaselineSnapshotSchema = z.object({
    /**
     * ISO 8601 string representing when the baseline snapshot was captured.
     */
    capturedAt: z.string(),
    /**
     * The live description HTML captured at baseline.
     */
    descriptionHtml: z.string().nullable().optional(),
    /**
     * The live variant prices captured at baseline (mapped by variant ID to its price string or number).
     */
    variantPrices: z
        .record(z.string(), z.union([z.string(), z.number()]))
        .nullable()
        .optional(),
    /**
     * The live product title captured at baseline, recorded for every target so the
     * recommendation card can name the product it is about to change. Display only —
     * the product title is not itself a testable field.
     */
    productTitle: z.string().nullable().optional(),
    /**
     * The live variant titles captured at baseline (mapped by variant ID to its display
     * title), so the apply-time variant picker can label variants without a live fetch.
     * Absent on bindings captured before apply-time variant selection existed; callers
     * fall back to the variant ID.
     */
    variantTitles: z.record(z.string(), z.string()).nullable().optional(),
    /**
     * The live product media URLs or GIDs captured at baseline.
     */
    productMedia: z.array(z.string()).nullable().optional(),
    /**
     * Display-only context for the results product preview. Present on the first binding of a
     * packet only. Absent on bindings captured before the preview shipped, which render degraded.
     */
    previewContext: productFieldPreviewContextSchema.nullable().optional(),
});
export const productFieldBindingSchema = z.object({
    /**
     * The Shopify product ID (canonical GID e.g. 'gid://shopify/Product/123456789').
     */
    productId: z.string(),
    /**
     * Optional list of Shopify variant IDs (canonical GIDs) if the binding is variant-scoped.
     */
    variantIds: z.array(z.string()).nullable().optional(),
    /**
     * The stimulus element type string (e.g., 'claim', 'price', etc.).
     */
    elementType: z.string(),
    /**
     * The resolved action and target for this binding.
     */
    resolution: productFieldBindingResolutionSchema,
    /**
     * The control arm/revert reference values snapshot from when the study was created.
     */
    baselineSnapshot: productFieldBaselineSnapshotSchema,
});
/**
 * Resolves a stimulus element type to its corresponding Shopify (action, target) pair.
 * Guarantees that any unknown or unmappable element types explicitly resolve to the advisory action and none target.
 */
export function resolveProductFieldBinding(elementType) {
    const normalized = elementType.toLowerCase().trim();
    const treatmentField = resolveShopifyTreatmentField(normalized);
    if (treatmentField)
        return treatmentField.resolution;
    switch (normalized) {
        case 'full_description':
        case 'description_variant':
        case 'description':
        case 'description_html':
            return { action: 'replace', target: 'description_html' };
        default:
            return { action: 'advisory', target: 'none' };
    }
}
/**
 * Creates a fully resolved ProductFieldBinding object from raw inputs.
 */
export function createProductFieldBinding(params) {
    const resolution = resolveProductFieldBinding(params.elementType);
    const capturedAt = params.capturedAt ?? new Date().toISOString();
    return {
        productId: params.productId,
        variantIds: params.variantIds ?? null,
        elementType: params.elementType,
        resolution,
        baselineSnapshot: {
            capturedAt,
            descriptionHtml: params.baselineValues.descriptionHtml ?? null,
            variantPrices: params.baselineValues.variantPrices ?? null,
            productMedia: params.baselineValues.productMedia ?? null,
            productTitle: params.baselineValues.productTitle ?? null,
            variantTitles: params.baselineValues.variantTitles ?? null,
            previewContext: params.previewContext ?? null,
        },
    };
}
//# sourceMappingURL=product-field-binding.js.map